import { type FormEvent, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { applyForStaff, listPractices, type ClinicalRole, type PracticeBasic } from '../../api/auth'
import { Button } from '../../components/ui/Button'
import { selectClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'

const ROLES: { value: ClinicalRole; label: string; requiresApproval: boolean }[] = [
  { value: 'veterinarian', label: 'Veterinarian', requiresApproval: true },
  { value: 'vet_technician', label: 'Veterinary Technician', requiresApproval: false },
  { value: 'receptionist', label: 'Receptionist', requiresApproval: false },
  { value: 'non_clinical', label: 'Non-Clinical Staff', requiresApproval: false },
]

interface Props {
  onClose: () => void
  onSuccess: () => void
}

export default function ApplyForStaff({ onClose, onSuccess }: Props) {
  const toast = useToast()
  const [practices, setPractices] = useState<PracticeBasic[]>([])
  const [practiceId, setPracticeId] = useState('')
  const [role, setRole] = useState<ClinicalRole>('receptionist')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    listPractices()
      .then(setPractices)
      .catch(() => setError('Failed to load practices. Please try again.'))
      .finally(() => setLoading(false))
  }, [])

  const selectedRole = ROLES.find(r => r.value === role)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!practiceId) { setError('Please select a practice'); return }
    setError(null)
    setSubmitting(true)
    try {
      await applyForStaff({ practice_id: parseInt(practiceId), clinical_role: role })
      toast.success(
        selectedRole?.requiresApproval
          ? 'Application submitted — a super admin will review it shortly.'
          : 'You have been added to the practice.'
      )
      onSuccess()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit application')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-card w-full max-w-[460px] p-8 relative">
        <button
          className="absolute top-4 right-4 text-text-3 hover:text-text transition-colors"
          onClick={onClose}
          type="button"
        >
          <X size={20} />
        </button>

        <h2 className="text-lg font-bold text-text-heading mb-1">Apply to join a practice</h2>
        <p className="text-sm text-text-3 mb-6">
          Select the practice and the role you are applying for.
        </p>

        {error && (
          <div className="text-danger-text text-sm mb-4 bg-danger-bg border border-danger-border rounded px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="practice">
            Practice
          </label>
          {loading ? (
            <div className="text-text-3 text-sm mb-4">Loading practices…</div>
          ) : practices.length === 0 ? (
            <div className="text-text-3 text-sm mb-4">No practices available yet.</div>
          ) : (
            <select
              id="practice"
              className={`${selectClass(false)} mb-4`}
              value={practiceId}
              onChange={(e) => setPracticeId(e.target.value)}
              required
            >
              <option value="">Select a practice…</option>
              {practices.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}

          <label className="block text-sm font-semibold text-text-heading mb-1" htmlFor="role">
            Role
          </label>
          <select
            id="role"
            className={`${selectClass(false)} mb-4`}
            value={role}
            onChange={(e) => setRole(e.target.value as ClinicalRole)}
          >
            {ROLES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>

          {selectedRole && (
            <p className="text-xs text-text-3 mb-6 bg-bg rounded px-3 py-2 border border-border">
              {selectedRole.requiresApproval
                ? 'This role requires verification by a super admin before access is granted.'
                : 'This role is approved automatically.'}
            </p>
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
            <Button
              type="submit"
              disabled={submitting || loading || practices.length === 0}
            >
              {submitting ? 'Submitting…' : 'Submit application'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
